import React, { useState, useEffect, useRef } from 'react';
import { Field, getIn } from 'formik';
import Link from '@nib-components/link';
import { formatDecimal } from '../../util/format-decimal';
import { Stack, Box } from '@nib/layout';
import Copy from '@nib-components/copy';
import Modal from '@nib-components/modal';
import { Help } from '@nib-components/form-control';
import { pl, Margin } from '@nib/layout';
import styled from 'styled-components';
import { TransferringFundsRadio, MemberNumber, CurrentProvider, NestedFormFields, SecondaryButton, ContinuousCover } from '../..';
import { AddFunnelPropFromContext } from '../funnel-context';
import { getGroupEventHandlerProps } from '../../util/propTypes';
import { FUNNELS, LABELS, INTERNATIONAL_FUNNELS } from '../../util/constants';
import Heading from '@nib-components/heading';
import { colorDarkest } from '@nib-components/theme';
import { mb } from '@nib/layout';
import { StandardInfoBox } from '../info-box';
const ARHI_AND_WHITELABEL_INFO = ['Add your current health fund’s details so we can cancel your current policy and get your transfer certificate. This will mean we can recognise waiting periods you’ve served and check remaining limits on your included services.', 'If you don’t provide these details, you will need to cancel your current cover and provide us with a transfer certificate. This could delay your ability to claim under your new cover.'];
const ARHI_AND_WHITELABEL_DEPENDANT_INFO = ['Add your dependant’s current health fund’s details so we can cancel their current policy and get their transfer certificate. This will mean we can recognise waiting periods they’ve served and check remaining limits on their included services.', 'If you don’t provide these details, they will need to cancel their current cover and provide us with a transfer certificate. This could delay their ability to claim under your new cover.'];
const STUDENTS_INFO = ['Let us know your previous OSHC insurer’s details and any waiting periods already served may be recognised so you can claim straight away.'];
const ARHI_AND_WHITELABEL_SUB_INFO = 'By providing these details, I authorise nib to cancel and obtain details of my current policy, and to request a transfer certificate for me and anyone else on my policy.';
const GU_SUB_INFO = ARHI_AND_WHITELABEL_SUB_INFO.replace('nib', 'GU Health');
const LHC_LOADING_LINK = 'https://www.nib.com.au/health-information/healthcare-in-australia/lifetime-health-cover';
const StyledUl = styled.ul`
  ${pl(5)}
`;
const FormSubHeading = styled(Heading)`
  color: ${colorDarkest};
  /* Bump up specificity */
  && {
    ${mb(6)};
  }
`;
export const DependentSubInfo = _ref => {
  let {
    funnel
  } = _ref;
  let brand;
  if (funnel === 'GU') {
    brand = 'GU Health';
  } else {
    brand = 'nib';
  }
  return React.createElement(React.Fragment, null, "By providing these details, I confirm that:", React.createElement(StyledUl, null, React.createElement("li", null, "I have the necessary authority to act on behalf of my child"), React.createElement("li", null, `I authorise ${brand} to cancel and obtain details of my child’s current policy and to request a transfer certificate for them`), React.createElement("li", null, `my child is aware that I am providing this authority to ${brand}`)));
};
const APIA_SUB_INFO = 'By providing these details, I authorise Apia to cancel and obtain details of my current policy, and to request a transfer certificate for me and anyone else on my policy.';
const INFO = {
  ARHI: ARHI_AND_WHITELABEL_INFO,
  IWHI: ARHI_AND_WHITELABEL_INFO,
  IMAN: ARHI_AND_WHITELABEL_INFO,
  ISHI: STUDENTS_INFO
};
const INFO_DEPENDANT = {
  ARHI: ARHI_AND_WHITELABEL_DEPENDANT_INFO
};
const SUB_INFO = {
  ARHI: ARHI_AND_WHITELABEL_SUB_INFO,
  APIA: APIA_SUB_INFO,
  IWHI: ARHI_AND_WHITELABEL_SUB_INFO,
  IMAN: ARHI_AND_WHITELABEL_SUB_INFO,
  ISHI: ARHI_AND_WHITELABEL_SUB_INFO,
  GU: GU_SUB_INFO
};
const SUB_INFO_DEPENDENT = {
  ARHI: DependentSubInfo,
  GU: DependentSubInfo
};
const PriceNotificationYes = _ref2 => {
  let {
    isPartner,
    newPrice
  } = _ref2;
  return React.createElement(React.Fragment, null, "Your price has been updated to reflect your ", isPartner ? "partner's " : '', "changed transfer details. ", React.createElement("strong", null, "New price $", formatDecimal(newPrice, {
    places: 2,
    separator: ','
  }), "*."));
};
const LHCModal = _ref3 => {
  let {
    show,
    toggleModal
  } = _ref3;
  return React.createElement(Modal, {
    visible: show,
    onClose: () => toggleModal(!show),
    title: 'Lifetime Health Cover'
  }, React.createElement(Box, {
    marginBottom: 7
  }, React.createElement(Copy, null, React.createElement(Link, {
    href: LHC_LOADING_LINK
  }, "Lifetime Health Cover (LHC)"), " is a Government initiative aimed at encouraging people to take out private hospital cover earlier in life, and maintain it. If you don\u2019t take out private hospital cover before 1 July after you turn 31, you\u2019ll pay a 2% loading on top of your normal hospital premium for each year you don\u2019t have hospital cover (up to a maximum of 70%). The loading applies for 10 years of continuous hospital cover. For every year you put off signing up for hospital cover, another 2% will be added. So if you wait until you\u2019re 40, you\u2019ll pay 20% more than someone on the same cover who joined when they were 31.")), React.createElement(SecondaryButton, {
    onClick: () => toggleModal(!show)
  }, "Close"));
};
const PriceNotificationNo = _ref4 => {
  let {
    newPrice,
    showLHCModal,
    toggleLHCModal
  } = _ref4;
  return React.createElement(React.Fragment, null, "Lifetime Health Cover (LHC) loading will increase your price. ", React.createElement("strong", null, "New price $", formatDecimal(newPrice, {
    places: 2,
    separator: ','
  }), "*."), ' ', React.createElement(Link, {
    onClick: () => toggleLHCModal(!showLHCModal),
    id: 'ga-inlink-lhc'
  }, "What's LHC?"));
};
const NestedFieldSpacing = styled.div`
  && > * {
    margin-bottom: 2rem;
  }
`;
const SubInfoMessage = props => {
  const {
    subInfoBoxMessage = LABELS.TRANSFERRING_FUNDS_SUB_HEADING,
    isComponent = false
  } = props;
  const SubInfoBoxMessage = subInfoBoxMessage;
  return React.createElement(React.Fragment, null, subInfoBoxMessage && React.createElement(StandardInfoBox, {
    withTip: false
  }, React.createElement(Copy, null, isComponent ? React.createElement(SubInfoBoxMessage, null) : subInfoBoxMessage)));
};
SubInfoMessage.defaultProps = {
  subInfoBoxMessage: LABELS.TRANSFERRING_FUNDS_SUB_HEADING
};
const ContinuousCoverHelpMessage = _ref5 => {
  let {
    show,
    toggleModal
  } = _ref5;
  return React.createElement(React.Fragment, null, React.createElement(Help, null, "The number of years you have held Hospital cover affects your ", React.createElement(Link, {
    onClick: () => toggleModal(!show)
  }, "Lifetime Health Cover (LHC)"), " loading."));
};
const TransferringFundsGroup = props => {
  const {
    id = 'transferringFunds',
    name = 'transferringFunds',
    funnel = FUNNELS.IWHI,
    subHeading = LABELS.TRANSFERRING_FUNDS_SUB_HEADING,
    infoBoxMessage,
    subInfoBoxMessage,
    values,
    enableContinuousCover,
    currentProviderHelp,
    currentProviderLabel,
    currentProviderOptions,
    currentProviderSuggestions,
    memberNumberLabel,
    memberNumberMaxLength,
    groupOnBlurHandlers = {},
    groupOnChangeHandlers = {},
    isPartner,
    newPrice,
    currentOfferTerms,
    showInfoBoxMessage = true,
    isDependant,
    policyHolderHasPreviousFund,
    partnerHasPreviousFund,
    showMemberInfoBoxForNibFund,
    required,
    ...otherProps
  } = props;
  const value = values ? values[id] : '';
  const previousPriceRef = useRef(newPrice);
  const [notificationToRender, setNotificationToRender] = useState(value);
  const [showLHCModal, toggleLHCModal] = React.useState(false);
  useEffect(() => {
    if (newPrice !== previousPriceRef.current) {
      previousPriceRef.current = newPrice;
      setNotificationToRender(value);
    }
  }, [newPrice]);
  const previousFundCode = useRef(getIn(values, `${id}--currentProvider`));
  const [hasFundCodeChanged, setHasFundCodeChanged] = useState(false);
  useEffect(() => {
    const latestFundCode = getIn(values, `${id}--currentProvider`);
    if (latestFundCode !== previousFundCode.current) {
      previousFundCode.current = latestFundCode;
      setHasFundCodeChanged(true);
    }
  }, [values]);
  const showSubfields = isDependant ? getIn(values, id) === 'YesDifferentFund' : value === 'yes';
  const radioSelected = Boolean(value && value.length);
  const subInfoMessageSet = isDependant ? SUB_INFO_DEPENDENT : SUB_INFO;
  const subInfoMessage = subInfoBoxMessage || subInfoMessageSet[funnel] || subInfoMessageSet.ARHI;
  const showPriceNotification = !INTERNATIONAL_FUNNELS.includes(funnel) && radioSelected && newPrice;
  const showFundChanged = !INTERNATIONAL_FUNNELS.includes(funnel) && currentOfferTerms && hasFundCodeChanged;
  const eventHandlers = {
    groupOnBlurHandlers,
    groupOnChangeHandlers
  };
  /*
  We can take strings or arrays, we use arrays to loop over and create multiple paragraphs etc.
  If a string comes in then we convert it to an array so we can simply loop over it further down.
  */
  let infoMessageArray = [];
  if (infoBoxMessage) {
    if (Array.isArray(infoBoxMessage)) {
      infoMessageArray = infoBoxMessage;
    } else {
      infoMessageArray.push(infoBoxMessage);
    }
  } else {
    infoMessageArray = isDependant ? INFO_DEPENDANT[funnel] || INFO_DEPENDANT.ARHI : INFO[funnel] || INFO.ARHI;
  }
  const policyHolderOrDependantId = isDependant ? 'childTransferringFunds' : id;
  return React.createElement(Stack, {
    space: 2
  }, React.createElement(LHCModal, {
    show: showLHCModal,
    toggleModal: toggleLHCModal
  }), React.createElement(Field, {
    required: required,
    name: name,
    id: id,
    component: TransferringFundsRadio,
    isDependant: isDependant,
    policyHolderHasPreviousFund: policyHolderHasPreviousFund,
    partnerHasPreviousFund: partnerHasPreviousFund,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: policyHolderOrDependantId
    }),
    ...otherProps
  }), showPriceNotification && React.createElement(StandardInfoBox, {
    role: "status",
    withTip: true
  }, React.createElement(Copy, null, notificationToRender === 'no' ? React.createElement(PriceNotificationNo, {
    newPrice: newPrice,
    showLHCModal: showLHCModal,
    toggleLHCModal: toggleLHCModal
  }) : React.createElement(PriceNotificationYes, {
    newPrice: newPrice,
    isPartner: isPartner
  }))), showInfoBoxMessage && React.createElement(Stack, {
    spaces: 2
  }, React.createElement(StandardInfoBox, {
    withTip: !showPriceNotification
  }, infoMessageArray.map((item, index) => React.createElement(Box, {
    marginBottom: 2,
    key: index
  }, item)))), React.createElement(NestedFormFields, {
    expanded: showSubfields,
    id: `${id}--TransferringFundsGroup`,
    showLeftBorder: !isDependant
  }, React.createElement(NestedFieldSpacing, {
    id: `${id}--TransferringFundsGroup`
  }, enableContinuousCover && showSubfields && React.createElement(Margin, {
    top: 7
  }, React.createElement(Field, {
    name: `${id}--continuousCover`,
    id: `${id}--continuousCover`,
    label: "How many years have you continuously held Hospital cover for?",
    help: React.createElement(ContinuousCoverHelpMessage, {
      show: showLHCModal,
      toggleModal: toggleLHCModal
    }),
    component: ContinuousCover,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: `${policyHolderOrDependantId}--continuousCover`
    })
  })), React.createElement(FormSubHeading, {
    size: 3
  }, subHeading), !INTERNATIONAL_FUNNELS.includes(funnel) && React.createElement(SubInfoMessage, {
    isComponent: isDependant,
    subInfoBoxMessage: subInfoMessage
  }), React.createElement(Stack, {
    spaces: 2
  }, React.createElement(Field, {
    required: required,
    name: `${name}--currentProvider`,
    id: `${id}--currentProvider`,
    component: CurrentProvider,
    options: currentProviderOptions,
    suggestions: currentProviderSuggestions,
    help: currentProviderHelp,
    label: currentProviderLabel,
    showMemberInfoBoxForNibFund: showMemberInfoBoxForNibFund,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: `${isDependant ? 'childTransferringFunds' : name}--currentProvider`
    })
  }), showFundChanged && React.createElement(StandardInfoBox, {
    withTip: true
  }, React.createElement(Copy, null, "Sorry, the offer is not available if your previous policy was underwritten by nib.", ' ', React.createElement(Link, {
    href: currentOfferTerms,
    target: "_blank",
    rel: "noopener"
  }, "See offer T&Cs"), "."))), React.createElement(Field, {
    name: `${name}--memberNumber`,
    id: `${id}--memberNumber`,
    component: MemberNumber,
    label: memberNumberLabel,
    maxLength: memberNumberMaxLength,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: `${isDependant ? 'childTransferringFunds' : name}--memberNumber`
    }),
    optional: true
  }), INTERNATIONAL_FUNNELS.includes(funnel) && React.createElement(SubInfoMessage, {
    isComponent: isDependant,
    subInfoBoxMessage: subInfoMessage
  }))));
};
TransferringFundsGroup.defaultProps = {
  subHeading: LABELS.TRANSFERRING_FUNDS_SUB_HEADING,
  funnel: 'IWHI',
  groupOnBlurHandlers: {},
  groupOnChangeHandlers: {},
  showInfoBoxMessage: true
};
const TransferringFundsGroupWithFunnel = props => React.createElement(AddFunnelPropFromContext, null, React.createElement(TransferringFundsGroup, {
  ...props
}));
export default TransferringFundsGroupWithFunnel;